import {
  backgroundColor,
  ringColor,
  ringOffsetColor,
  borderColor,
  textColor,
  placeholderColor,
} from './lib/design-tokens/colors';
import isNumber from 'lodash/isNumber';
import round from 'lodash/round';
import forms from '@tailwindcss/forms';
import containerQueries from '@tailwindcss/container-queries';
import type { Config } from 'tailwindcss';

const DEFAULT_FONT_SIZE = 16;

/**
 * Convert a unitless `px` number to em units, relative to `baseSize`
 *
 * @param {number} px
 * @param {number} [baseSize=(DEFAULT_FONT_SIZE)]
 * @param {'em' | 'rem'} unit
 */
const pxToEm = (
  px: number,
  baseSize: number = DEFAULT_FONT_SIZE,
  unit: 'em' | 'rem' = 'em',
) => {
  if (!isNumber(px)) {
    throw new TypeError(
      'Expected argument `px` to be numeric, without CSS units',
    );
  }
  return `${round(px / baseSize, 2)}${unit}`;
};

/**
 * Convert a unitless `px` number to rem units
 *
 * @param {number} px
 */
const pxToRem = (px: number) => pxToEm(px, DEFAULT_FONT_SIZE, 'rem');

export default {
  mode: 'jit',
  content: [
    './src/**/*.{js,jsx,ts,tsx}',
    './src/index.html',
    '../../libs/end-to-end/**/*.{js,jsx,ts,tsx}',
  ],
  darkMode: 'class',
  theme: {
    screens: {
      // Please do not change these values to em or rem. They are fixed width and pixels are already aware of DPI for the sake of media queries.
      // Also, you may find usages of these inside `useBreakpoints` if you are curious why the fixed range is important for our application.
      sm: '0px', // ~ mobile
      md: '768px', // ~ tablet portrait
      lg: '1024px', // ~ tablet landscape
      xl: '1366px', // ~ laptop
      '2xl': '1600px', // ~ desktop monitor
      '3xl': '1920px', // ~ TV
    },
    fontFamily: {
      serif: [
        '"Lora"', // Google Fonts
        // Fallbacks
        'Times New Roman',
        'Times',
        'serif',
      ],
      sans: [
        '"Karla"', // Google Fonts
        'Trebuchet MS', // macOS and Windows (Microsoft)
        'ui-sans-serif',
        '-apple-system', // "San Francisco" on newer OSX/iOS, Helvetica Neue and Lucida Grande on older
        'BlinkMacSystemFont', // macOS Chrome
        '"Helvetica Neue"', // macOS versions < 10.11
        '"Segoe UI"', // Windows
        'Arial', // most systems have this
        'Roboto', // Android, ChromeOS
        'Oxygen', // KDE Linux
        'Ubuntu', // Ubuntu LInux
        'Cantarell', // Gnome
        'sans-serif',
      ],
    },
    fontSize: {
      xxs: [pxToRem(12), pxToRem(16)],
      xs: [pxToRem(14), pxToRem(18)],
      sm: [pxToRem(16), pxToRem(21)],
      base: [pxToRem(18), pxToRem(24)],
      lg: [pxToRem(20), pxToRem(30)],
      xl: [pxToRem(24), pxToRem(36)],
      '2xl': [pxToRem(32), pxToRem(48)],
      '3xl': [pxToRem(40), pxToRem(60)],
      '4xl': [pxToRem(48), pxToRem(72)],
    },
    extend: {
      // Default spacing: https://tailwindcss.com/docs/customizing-spacing#default-spacing-scale
      backgroundColor,
      ringColor,
      borderColor,
      textColor,
      placeholderColor,
      ringOffsetColor,
      boxShadow: {
        '2px': '0px 2px 4px rgba(41, 45, 51, 0.16)',
        '4px': '0px 4px 8px rgba(41, 45, 51, 0.16)',
        '8px': '0px 8px 12px rgba(41, 45, 51, 0.16)',
        '12px': '0px 12px 16px rgba(41, 45, 51, 0.16)',
        '16px': '0px 16px 20px rgba(41, 45, 51, 0.16)',
        tooltip: '0px 4px 8px 0px rgba(41, 45, 51, 0.08)',
        soft: '12px 12px 24px 2px rgba(58, 58, 232, 0.04)',
        'soft-xl': '0 4px 32px 0 rgba(0, 0, 0, 0.12)',
        hard: '0 4px 20px 0 rgba(8, 8, 87, 0.08)',
      },
      backgroundImage: {
        gradient: 'linear-gradient(136.29deg, #400977 50%, #CE106F 161.31%)',
        'radial-top-soft':
          'radial-gradient(100% 100% at 50% 0%, #F2F2F9 0%, #F7F7F7 100%)',
      },
      borderWidth: {
        1: pxToRem(1),
      },
      borderRadius: {
        '4xl': '32px',
        '2xl': '16px',
      },
      minHeight: {
        20: pxToRem(80),
      },
      width: {
        1200: '1200px',
      },
      zIndex: {
        standout: '10',
        cover: '20',
        tooltip: '30',
        'tooltip-standout': '31',
        overlay: '40',
        'overlay-standout': '50',
        'overlay-cover': '60',
        'overlay-tooltip': '70',
        'scrollbar-track': '80',
      },
    },
    keyframes: {
      pulseStrong: {
        '0%, 100%': { opacity: '1', transform: 'scale(1)' },
        '50%': { opacity: '0.25', transform: 'scale(0.85)' },
      },
    },
    animation: {
      'pulse-strong': 'pulseStrong 2s ease-in-out infinite',
    },
  },
  variants: {
    extend: {
      backgroundColor: [
        'active',
        'focus',
        'focus-visible',
        'disabled',
        'checked',
      ],
      borderColor: ['active', 'focus', 'focus-visible', 'disabled'],
      borderWidth: ['active', 'focus', 'focus-visible'],
      textColor: ['active', 'focus-visible', 'disabled'],
      ringColor: ['focus', 'focus-visible'],
      ringOffsetColor: ['focus', 'focus-visible'],
      ringOffsetWidth: ['focus', 'focus-visible'],
      ringWidth: ['focus', 'focus-visible'],
      ringOpacity: ['focus', 'focus-visible'],
      outline: ['focus', 'focus-visible'],
      cursor: ['disabled'],
    },
  },
  plugins: [
    forms({
      strategy: 'class',
    }),
    containerQueries,
  ],
} satisfies Config;
